import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, tap } from "rxjs";
import { environment } from "../../../../environments/environment";
import {
    BankDetailsValue,
    ClaimDetailByIdDto,
    ClaimFormValue,
    ClaimStatusDto,
    DocumentsFormValue,
    IncidentDetailsValue,
    PersonalDetailsValue,
    SubmitClaimDto,
} from "../_interfaces/claim.interface";
import { ClaimDocumentType } from "../../../shared/constants/documents.constant";
import { DecodeClaimTokenResponse, PolicyDetailDto } from "../../../shared/interfaces/claim.interface";

@Injectable({
    providedIn: "root",
})
export class ClaimsService {
    private baseUrl = `${environment.url}/Claim`;
    private http = inject(HttpClient);

    getPolicyDetailById$(id: string): Observable<PolicyDetailDto> {
        return this.http.get<PolicyDetailDto>(`${this.baseUrl}/GetPolicyDetailById/policy/${id}`);
    }

    decodeToken$(idToken: string): Observable<DecodeClaimTokenResponse> {
        return this.http.post<DecodeClaimTokenResponse>(`${this.baseUrl}/DecodeToken`, { idToken }).pipe(
            tap((res) => {
                localStorage.setItem("claim_token_data", JSON.stringify(res));
            }),
        );
    }

    submitClaim$(policyId: string, claimFormValue: ClaimFormValue): Observable<SubmitClaimDto> {
        const formData = new FormData();

        formData.append("PolicyId", policyId);
        const { identityDocument, ...personalDetails } =
            claimFormValue.personalDetails ?? ({} as PersonalDetailsValue);
        for (const key in personalDetails) {
            formData.append(
                `PersonalDetails[${key}]`,
                personalDetails[key as keyof typeof personalDetails] as string,
            );
        }
        for (const key in claimFormValue.bankDetails) {
            formData.append(`BankDetails[${key}]`, claimFormValue.bankDetails[key as keyof BankDetailsValue]);
        }
        formData.append("ClaimType", claimFormValue.serviceRequestDetails!.serviceType!);
        for (const key in claimFormValue.incidentDetails) {
            // Special handling for incidentDateTime
            if (key === "incidentDateTime") {
                const formattedDateTime = claimFormValue.incidentDetails.incidentDateTime!.toISOString();
                formData.append(`IncidentDetails[${key}]`, formattedDateTime);
                continue;
            }

            formData.append(
                `IncidentDetails[${key}]`,
                claimFormValue.incidentDetails[key as keyof IncidentDetailsValue] as string,
            );
        }
        let docIndex = 0;
        for (const key in claimFormValue.documents) {
            const files = claimFormValue.documents[key];
            if (!files || files.length === 0) continue;
            formData.append(`ClaimDocuments[${docIndex}].Code`, key);
            for (const file of files) {
                formData.append(`ClaimDocuments[${docIndex}].Files`, file);
            }
            docIndex++;
        }
        // The IC/Passport upload lives in Personal Details but is submitted as a claim document.
        if (identityDocument?.length) {
            formData.append(`ClaimDocuments[${docIndex}].Code`, ClaimDocumentType.COPY_IC_PASSPORT);
            for (const file of identityDocument) {
                formData.append(`ClaimDocuments[${docIndex}].Files`, file);
            }
            docIndex++;
        }

        // FROM LOCAL STORAGE
        const tokenDataString = localStorage.getItem("claim_token_data");
        if (tokenDataString) {
            const tokenData = JSON.parse(tokenDataString) as DecodeClaimTokenResponse;
            formData.append("PipoClaimId", tokenData.pipoClaimId);
            formData.append("Language", tokenData.language);
            formData.append("RiskDecision", tokenData.riskDecision ?? "");
        }

        return this.http.post<SubmitClaimDto>(`${this.baseUrl}/SubmitClaim`, formData);
    }

    getClaimByPipoClaimId$(pipoClaimId: string): Observable<ClaimStatusDto> {
        return this.http.get<ClaimStatusDto>(`${this.baseUrl}/GetClaimByPipoClaimId/${pipoClaimId}`);
    }

    getClaimDetailByClaimId$(claimId: string): Observable<ClaimDetailByIdDto> {
        return this.http.get<ClaimDetailByIdDto>(`${this.baseUrl}/GetClaimDetailByClaimId/${claimId}`);
    }

    getSupplementDocuments$(claimId: string): Observable<ClaimDocumentType[]> {
        return this.http.get<ClaimDocumentType[]>(`${this.baseUrl}/GetSupplementDocuments/${claimId}`);
    }

    uploadSupplementDocuments$(claimId: string, documentsFormValue: DocumentsFormValue) {
        const formData = new FormData();

        formData.append("ClaimId", claimId);

        let docIndex = 0;
        for (const key in documentsFormValue.documents) {
            const files = documentsFormValue.documents[key];
            if (!files || files.length === 0) continue;
            formData.append(`ClaimDocuments[${docIndex}].Code`, key);
            for (const file of files) {
                formData.append(`ClaimDocuments[${docIndex}].Files`, file);
            }
            docIndex++;
        }

        return this.http.post<SubmitClaimDto>(`${this.baseUrl}/UploadSupplementDocuments`, formData);
    }
}
